/**
 * Keep a prebuilt gift box and its `_ub_grp` add-ons in step, in the cart.
 *
 * Two things bb-cart.js does per-line that should be per-GIFT:
 *   quantity  — raising the box left its add-ons behind at the old count
 *   removal   — removing the box left its add-ons in the cart as loose items
 *
 * It also splits multi-quantity gifts on the way to the multi-address page —
 * see SPLIT BEFORE LEAVING at the bottom.
 *
 * THE BUG THIS FIXES
 * A prebuilt box's quantity stepper (snippets/bb-cart-prebuilt.liquid) carries
 * a single data-key — the box line — and bb-cart.js changes that one line.
 * Its cart-added add-ons, linked only by `_ub_grp`, are left alone. So raising
 * a box to 2 produced a cart with two boxes and ONE add-on: an order that
 * ships a gift without the thing the customer added to it.
 *
 * A builder box has never had this problem. It stores `_bb_unit_qty` per line
 * and setGroupCopies() multiplies every line in the group together. This is the
 * same idea for the `_ub_grp` path, which has no equivalent.
 *
 * WHY A SEPARATE FILE
 * bb-cart.js is ~60KB. Editing it means rewriting the whole thing, which is how
 * assets/bb-multiship.js was truncated and lost on 2026-10-01. This hooks the
 * same buttons from outside instead: capture-phase listener, so it runs before
 * bb-cart.js's own delegated handler and can stop it.
 *
 * SCOPE — deliberately narrow. It only acts when ALL of these hold:
 *   - the click is on a prebuilt box card's own stepper
 *   - that box line carries a non-blank `_ub_grp`
 *   - at least one OTHER line shares that `_ub_grp` and has no `_bundle_note`
 * Anything else — a builder box, a loose item, a prebuilt box with no add-ons —
 * falls straight through to the existing handler, untouched.
 */
(function () {
  "use strict";

  var BUSY = false;

  function grpOf(item) {
    var g = item && item.properties ? item.properties._ub_grp : null;
    if (g == null) return "";
    return String(g).trim();
  }

  function isBoxLine(item) {
    var n = item && item.properties ? item.properties._bundle_note : null;
    return !!(n != null && String(n).trim() !== "");
  }

  function hasBuilderId(item) {
    var b = item && item.properties ? item.properties._bb_box_id : null;
    return !!(b != null && String(b).trim() !== "");
  }

  /* Reload without the browser restoring scroll.

     Every path here ends in a reload, and the page is SHORTER afterwards — a
     gift has gone, or its add-ons have. Chrome restores the old pixel offset
     into a shorter document, which dropped the customer into the reviews
     carousel below the cart. scrollRestoration = "manual" tells it not to,
     so the cart is where they left off: at the top of it. */
  function reloadAtTop() {
    var api = window.BBCartRender;
    if (api && typeof api.refresh === "function") { BUSY = false; api.refresh(); return; }
    try { if ("scrollRestoration" in history) history.scrollRestoration = "manual"; } catch (e) {}
    window.location.reload();
  }

  /* A quantity change leaves the page the same height — same gifts, same
     cards, one number different — so the browser's own scroll restoration
     puts the customer back exactly where they were looking. Only REMOVAL
     needs reloadAtTop(), because the card they were looking at is gone and a
     restored offset lands somewhere arbitrary. */
  function reloadKeepScroll() {
    var api = window.BBCartRender;
    if (api && typeof api.refresh === "function") { BUSY = false; api.refresh(); return; }
    try { if ("scrollRestoration" in history) history.scrollRestoration = "auto"; } catch (e) {}
    window.location.reload();
  }

  function getCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); });
  }

  /* How many of this add-on belong in ONE box.

     There is no stored per-copy quantity for the `_ub_grp` path, so it is
     derived from the state we find: an add-on sitting at 3 against a box at 3
     is one per box; at 6 against 3 it is two per box. Rounding settles the
     already-broken carts this bug produced — an add-on at 1 against a box at 2
     reads as one per box, which is what the customer meant when they added it
     "into this box", and the next change writes that through. */
  function unitQtyOf(extraQty, boxQty) {
    var per = Math.round((parseInt(extraQty, 10) || 1) / Math.max(1, parseInt(boxQty, 10) || 1));
    return Math.max(1, per);
  }

  function applyGroupQuantity(boxKey, delta) {
    return getCart().then(function (cart) {
      var items = (cart && cart.items) || [];

      var box = null;
      items.forEach(function (it) { if (it.key === boxKey) box = it; });
      if (!box) return null;

      var grp = grpOf(box);
      if (!grp || !isBoxLine(box)) return null;

      var extras = items.filter(function (it) {
        return it.key !== boxKey && !hasBuilderId(it) && grpOf(it) === grp && !isBoxLine(it);
      });
      if (!extras.length) return null;   /* nothing to keep in step */

      var oldQty = Math.max(1, parseInt(box.quantity, 10) || 1);
      var newQty = oldQty + delta;
      if (newQty < 1) newQty = 1;
      if (newQty === oldQty) return null;

      var updates = {};
      updates[box.key] = newQty;
      extras.forEach(function (ex) {
        updates[ex.key] = unitQtyOf(ex.quantity, oldQty) * newQty;
      });

      return fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates: updates })
      }).then(function (r) { return r.json(); });
    });
  }

  document.addEventListener("click", function (e) {
    if (BUSY) return;

    var btn = e.target.closest && e.target.closest("[data-bb-line-inc], [data-bb-line-dec]");
    if (!btn) return;

    /* Prebuilt box cards only. A builder box's stepper is data-bb-box-qty-* and
       is already group-aware, and an add-on row's own × button is not a stepper
       at all. */
    var card = btn.closest(".bb-cart-box--prebuilt");
    if (!card) return;

    var holder = btn.closest("[data-bb-line-qty]");
    var boxKey = holder && holder.getAttribute("data-key");
    if (!boxKey) return;

    var delta = btn.hasAttribute("data-bb-line-inc") ? 1 : -1;

    /* We do not yet know whether this box has add-ons — that needs the cart.
       Rather than block the click on a network round trip, let the default
       handler run for a box with none. So: read first, and only take over if
       there is something to scale. Taking over means re-doing the box line
       ourselves, which is why the default is stopped here. */
    e.preventDefault();
    e.stopImmediatePropagation();

    BUSY = true;
    applyGroupQuantity(boxKey, delta)
      .then(function (result) {
        if (result) {
          /* The page re-renders from the cart on load. A reload is blunt but it
             is the only refresh path that is certain to exist on both the cart
             page and the drawer — bb-cart.js's own refresh is internal to it. */
          reloadKeepScroll();
          return;
        }
        /* No add-ons: nothing special to do, but we already swallowed the
           click, so perform the plain single-line change the default handler
           would have. */
        return getCart().then(function (cart) {
          var items = (cart && cart.items) || [];
          var box = null;
          items.forEach(function (it) { if (it.key === boxKey) box = it; });
          if (!box) return;
          var q = Math.max(1, (parseInt(box.quantity, 10) || 1) + delta);
          var updates = {};
          updates[box.key] = q;
          return fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ updates: updates })
          }).then(function () { reloadKeepScroll(); });
        });
      })
      .catch(function () {
        BUSY = false;
        reloadKeepScroll();
      });
  }, true);

  /* ------------------------------------------------------------------
     REMOVAL

     The prebuilt card's footer Remove carries the BOX line's key, and
     bb-cart.js removes exactly that line. Its add-ons, linked only by
     `_ub_grp`, stay in the cart as loose items — a Bro Mask and a Fudge
     Brownie sitting on their own with no gift to belong to.

     Only the card's own Remove is intercepted. Each add-on row has its own ×
     with its own data-key, and removing one of those should remove just it —
     so the test is on the LINE, not the markup: cascade only when the key
     belongs to a line carrying `_bundle_note`, which is the box.

     A builder box's footer uses data-bb-box-remove and is already group-aware
     in bb-cart.js, so it never reaches this.
     ------------------------------------------------------------------ */
  document.addEventListener("click", function (e) {
    if (BUSY) return;

    var btn = e.target.closest && e.target.closest("[data-bb-line-remove]");
    if (!btn) return;

    var key = btn.getAttribute("data-key");
    if (!key) return;

    var card = btn.closest(".bb-cart-box--prebuilt");
    if (!card) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    BUSY = true;
    getCart()
      .then(function (cart) {
        var items = (cart && cart.items) || [];

        var target = null;
        items.forEach(function (it) { if (it.key === key) target = it; });
        if (!target) return null;

        var updates = {};
        updates[key] = 0;

        /* Only a box line takes its siblings with it. An add-on's own × is a
           single-line removal, exactly as before. */
        if (isBoxLine(target)) {
          var grp = grpOf(target);
          if (grp) {
            items.forEach(function (it) {
              if (it.key === key) return;
              if (hasBuilderId(it)) return;
              if (grpOf(it) !== grp) return;
              if (isBoxLine(it)) return;      /* another box in the same group */
              updates[it.key] = 0;
            });
          }
        }

        return fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ updates: updates })
        }).then(function (r) { return r.json(); });
      })
      .then(function () { reloadAtTop(); })
      .catch(function () { BUSY = false; reloadAtTop(); });
  }, true);

  /* ==================================================================
     SPLIT BEFORE LEAVING FOR THE MULTI-ADDRESS PAGE

     Shopify merges identical lines, so "3 Firesides with the same card" is ONE
     line of quantity 3. One line carries one address, so that customer cannot
     send them to three people — which is the entire point of the page they are
     heading to.

     bb-multiship.js can split them once they arrive, but the cart has to be
     rewritten and the app's rendered table does not follow a cart write, so it
     has to reload the page. The customer sees the gifts appear, rearrange and
     reload: it reads as the page glitching.

     Doing it HERE removes that. The split happens during the navigation the
     customer already expects to take a moment, and they arrive at a page that
     is correct on its first render. Same work, no visible reload.

     HOW THE HANDOFF WORKS. The button runs an Alpine handler
     (@click.prevent="$store.cart.onProductAttributesUpdate('MULTIPLE_ADDRESS')").
     Rather than reimplement that, this swallows the first click, does the
     split, then re-fires the SAME control with a flag set so this handler
     stands aside the second time and Alpine's own handler runs normally.
     ================================================================== */

  var SPLIT_CAP = 20;          /* matches bb-multiship.js */
  var handingOff = false;      /* true while we re-fire the customer's click */

  function newGroupId() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  function propsCopy(item, key, value) {
    var out = {};
    var src = item.properties || {};
    Object.keys(src).forEach(function (k) {
      if (src[k] != null && src[k] !== "") out[k] = src[k];
    });
    out[key] = value;
    return out;
  }

  function addLine(variantId, qty, props) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: variantId, quantity: qty, properties: props })
    }).then(function (r) { return r.json(); });
  }

  function updateLines(updates) {
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  function runSequential(list, fn) {
    var i = 0;
    function next() {
      if (i >= list.length) return Promise.resolve();
      return Promise.resolve(fn(list[i++])).then(next);
    }
    return next();
  }

  /* Every gift of 2+ in the cart, as a plan of what to add and what to reduce.
     Returns null when there is nothing to do, so the click passes straight
     through untouched. */
  function planSplit(items) {
    var plan = [];
    var totalGifts = 0;

    /* Builder gifts: lines sharing _bb_box_id, copies = the box line's qty,
       per-copy quantities already stored as _bb_unit_qty. */
    var builder = {};
    items.forEach(function (it) {
      var id = it.properties && it.properties._bb_box_id;
      if (!id) return;
      id = String(id).trim();
      if (!id) return;
      (builder[id] = builder[id] || []).push(it);
    });
    Object.keys(builder).forEach(function (id) {
      var group = builder[id];
      var box = null;
      group.forEach(function (it) {
        if (String((it.properties && it.properties._bb_role) || "") === "box") box = it;
      });
      var copies = Math.max(1, parseInt((box || group[0]).quantity, 10) || 1);
      if (copies < 2) return;
      totalGifts += copies;
      plan.push({ kind: "bb", key: "_bb_box_id", prefix: "bb-", copies: copies, lines: group.map(function (it) {
        var unit = parseInt(it.properties && it.properties._bb_unit_qty, 10);
        if (!(unit > 0)) unit = Math.max(1, Math.round((parseInt(it.quantity, 10) || 1) / copies));
        return { item: it, unit: unit };
      }) });
    });

    /* Prebuilt gifts: the box line plus the extras sharing its _ub_grp. */
    items.forEach(function (box) {
      if (hasBuilderId(box) || !isBoxLine(box)) return;
      var copies = Math.max(1, parseInt(box.quantity, 10) || 1);
      if (copies < 2) return;
      var grp = grpOf(box);
      var extras = !grp ? [] : items.filter(function (it) {
        return it.key !== box.key && !hasBuilderId(it) && grpOf(it) === grp && !isBoxLine(it);
      });
      totalGifts += copies;
      plan.push({ kind: "ub", key: "_ub_grp", prefix: "", copies: copies,
        lines: [{ item: box, unit: 1 }].concat(extras.map(function (ex) {
          return { item: ex, unit: Math.max(1, Math.round((parseInt(ex.quantity, 10) || 1) / copies)) };
        })) });
    });

    if (!plan.length) return null;
    if (totalGifts > SPLIT_CAP) return null;   /* bb-multiship.js offers the button instead */
    return plan;
  }

  function runSplit(plan) {
    var jobs = [];
    plan.forEach(function (gift) {
      for (var c = 1; c < gift.copies; c++) {
        (function () {
          var gid = gift.prefix + newGroupId();
          jobs.push(function () {
            return runSequential(gift.lines, function (ln) {
              return addLine(ln.item.variant_id, ln.unit, propsCopy(ln.item, gift.key, gid));
            });
          });
        })();
      }
    });

    return runSequential(jobs, function (job) { return job(); }).then(function () {
      /* Originals come down LAST. A failure part-way leaves too many gifts —
         visible and refundable — rather than too few. */
      var updates = {};
      plan.forEach(function (gift) {
        gift.lines.forEach(function (ln) { updates[ln.item.key] = ln.unit; });
      });
      return updateLines(updates);
    });
  }

  document.addEventListener("click", function (e) {
    if (handingOff) return;     /* our own re-fire: let Alpine have it */

    var el = e.target.closest && e.target.closest(
      "[data-ub-ms-btn], .bb-cart-summary__multi-btn, label[for='ms__toggle-shipping']"
    );
    if (!el) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    /* Hand the click back to the app's own Alpine handler, with a fallback.

       OBSERVED: after our split, re-firing the control got as far as the app's
       "Taking you to multiple addresses…" spinner and then hung — its flow
       does not cope with the cart having changed under it a moment earlier.
       The spinner sat there forever, which is worse than the reload this was
       meant to remove.

       So: re-fire as before, but if we are still on this page a few seconds
       later, navigate to the multi-address page directly. The app initialises
       itself on arrival — that path is well travelled, it is what a reload
       has been doing all along. */
    var MS_URL = "/a/ms/cart/";

    /* OWN THE OVERLAY.

       The app's own spinner says "Taking you to multiple addresses..." the
       instant the control is clicked, which is a promise it cannot keep: the
       split below runs first, and the app's navigation may not fire at all.
       The customer reads a stalled promise as a broken page.

       So we drive the text ourselves. The overlay markup is the theme's
       (.ub-ms-loader > .ub-spin + .ub-load-txt) so it is already styled; this
       only writes into it, creating it if the app has not. An interval,
       because the app re-renders the overlay after our click and would
       otherwise put its own sentence back. The escape link appears if we are
       still here after SLOW_MS, so a slow load never looks like a dead one. */
    var SLOW_MS = 7000;
    var msgTimer = null;
    var started = Date.now();


    /* KILL THE SECOND SPINNER.

       While the overlay is up the page is covered and nothing underneath is
       clickable, so a wait/progress cursor is pure noise on top of the spinner
       the customer is already looking at. The theme sets it on descendants
       (#ms__toggle-box.is-busy among them), so an override on <html> would not
       reach it - hence a stylesheet rule broad enough to cover everything, and
       pulled again the moment the overlay goes.

       Note: the browser's own throbber in the tab, and the OS loading cursor
       during a real navigation, are not ours to remove. This only stops the
       page ADDING a second one of its own. */
    function cursorQuiet(on) {
      try {
        var id = "ub-ms-cursor-quiet";
        var el = document.getElementById(id);
        if (!on) {
          if (el && el.parentNode) el.parentNode.removeChild(el);
          return;
        }
        if (el) return;
        el = document.createElement("style");
        el.id = id;
        el.textContent =
          "html,body,body *{cursor:default !important;}" +
          ".ub-ms-loader a,.ub-ms-loader button{cursor:pointer !important;}";
        (document.head || document.documentElement).appendChild(el);
      } catch (e) {}
    }

    function overlayText(line, sub) {
      cursorQuiet(true);
      var ov = document.querySelector(".ub-ms-loader");
      if (!ov) {
        ov = document.createElement("div");
        ov.className = "ub-ms-loader";
        ov.innerHTML = '<div class="ub-spin"></div><div class="ub-load-txt"></div>';
        document.body.appendChild(ov);
        window.requestAnimationFrame(function () { ov.classList.add("ub-on"); });
      }
      var t = ov.querySelector(".ub-load-txt");
      if (t && t.textContent !== line) t.textContent = line;

      var s2 = ov.querySelector(".ub-load-sub");
      if (sub) {
        if (!s2) {
          s2 = document.createElement("div");
          s2.className = "ub-load-sub";
          s2.style.cssText = "margin-top:4px;font-size:12px;line-height:1.5;color:#9a8f88;text-align:center;";
          ov.appendChild(s2);
        }
        if (s2.textContent !== sub) s2.textContent = sub;
      } else if (s2 && s2.parentNode) {
        s2.parentNode.removeChild(s2);
      }

      if (Date.now() - started > SLOW_MS && !ov.querySelector(".ub-load-go")) {
        var a = document.createElement("a");
        a.className = "ub-load-go";
        a.href = MS_URL;
        a.textContent = "Continue \u2192";
        a.style.cssText =
          "display:inline-block;margin-top:12px;padding:9px 18px;border-radius:999px;" +
          "border:1.5px solid #E2D6CF;background:#fff;color:#2b2320;font-size:13px;" +
          "font-weight:700;text-decoration:none;";
        ov.appendChild(a);
      }
    }

    function driveOverlay(line, sub) {
      overlayText(line, sub);
      if (msgTimer) window.clearInterval(msgTimer);
      msgTimer = window.setInterval(function () {
        if (Date.now() - started > SLOW_MS) {
          overlayText("Almost there\u2026", "");
        } else {
          overlayText(line, sub);
        }
      }, 250);
    }

    function handOff() {
      handingOff = true;
      driveOverlay("Getting your gifts ready\u2026", "");
      try { el.click(); } catch (e) {}
      /* The app's handler does not cope with the cart having changed under it
         a moment ago, and when it stalls it stalls silently. Two and a half
         seconds is long enough for a working handoff to have started and short
         enough that a broken one is not felt as a hang. */
      window.setTimeout(function () {
        if (document.visibilityState === "hidden") return;   /* already leaving */
        window.location.assign(MS_URL);
      }, 2500);
    }

    /* One line, and no explanation of what is happening to the cart. A second
       line about separating the gifts would be wrong for most customers, who
       already have them as separate lines with their own cards — nothing is
       being separated for them. */
    driveOverlay("Getting your gifts ready\u2026", "");

    getCart()
      .then(function (cart) {
        var plan = planSplit((cart && cart.items) || []);
        if (!plan) { handOff(); return; }
        return runSplit(plan).then(handOff, handOff);
      })
      .catch(handOff);
  }, true);
})();